// src/components/HomePage/HomePage.js
import React, { useState } from 'react';
import ZipcodeModal from './ZipcodeModal';
import VideoBanner from './VideoBanner';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import HowItWorks from './HowItWorks';

const HERO_IMAGE =
  'https://res.cloudinary.com/dc6xthiol/image/upload/q_auto,f_auto/v1743405483/20241227_172600_skquzn.jpg';

// Cloudinary sirve cada foto ya recortada/optimizada (q_auto,f_auto,w_900)
const cdn = (path) => `https://res.cloudinary.com/dc6xthiol/image/upload/q_auto,f_auto,w_900/${path}`;

const FEATURES = [
  { n: 1, img: cdn('v1766032270/HP2_nt4ev2.jpg'), altKey: 'home.galleryAlt1' },
  { n: 2, img: cdn('v1783546408/20250920_175554_ei6x3v.jpg'), altKey: 'home.galleryAlt2' },
  { n: 3, img: cdn('v1766032270/HP4_g9jbfq.jpg'), altKey: 'home.galleryAlt3' },
  { n: 4, img: cdn('v1783569654/20260507_030721_dfp87d.jpg'), altKey: 'home.feature4Alt' },
  { n: 5, img: cdn('v1783546638/20251204_162826_008_doyi38.jpg'), altKey: 'home.feature5Alt' },
  { n: 6, img: cdn('v1783544353/20260213_182239_fcujqi.jpg'), altKey: 'home.feature6Alt' },
];

const HomePage = () => {
  const { t } = useTranslation('common');
  const [modalOpen, setModalOpen] = useState(false);

  return (
    <>
      <Helmet>
        <title>{t('meta.home.title')}</title>
        <meta name="description" content={t('meta.home.description')} />
        <link rel="canonical" href="https://www.hotelmacarios.com.mx/" />
      </Helmet>

      <div className="bg-[#EFE6DD]">
        {/* Hero Section - pantalla casi completa en móvil, texto anclado abajo */}
        <div
          className="relative flex h-[calc(100svh-4rem)] min-h-[520px] max-h-[900px] items-end justify-center bg-cover bg-bottom md:h-[620px] md:items-center"
          style={{ backgroundImage: `url('${HERO_IMAGE}')` }}
        >
          <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/25 to-black/10 md:bg-black/40 md:bg-none" />
          <div className="relative w-full max-w-3xl px-6 pb-[max(3rem,env(safe-area-inset-bottom))] text-center text-[#EFE6DD] md:pb-0">
            <h1 className="mb-3 font-['Fraunces'] text-5xl font-semibold leading-[1.05] tracking-tight sm:text-6xl md:mb-4 md:font-sans md:font-bold">
              {t('home.heroTitle')}
            </h1>
            <p className="mx-auto mb-8 max-w-md text-lg text-[#EFE6DD]/90 md:max-w-none md:text-2xl">
              {t('home.heroSubtitle')}
            </p>
            <button
              onClick={() => setModalOpen(true)}
              className="inline-flex w-full max-w-xs items-center justify-center rounded-full bg-[#9A0002] px-8 py-4 text-base font-semibold text-[#EFE6DD] shadow-lg shadow-black/30 transition duration-200 hover:bg-[#7A0001] active:scale-[0.97] md:w-auto md:rounded-lg md:py-3"
            >
              {t('home.bookNow')}
            </button>
          </div>
        </div>

        <HowItWorks onOpenZipcodeModal={() => setModalOpen(true)} />

        {/* Features Section – 6 fotos grandes con el texto encima */}
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
          <div className="mb-10 text-center md:mb-12">
            <h2 className="font-['Fraunces'] text-3xl font-semibold text-gray-800 md:text-4xl">
              {t('home.whyChooseUs')}
            </h2>
            <p className="mt-2 text-gray-500">{t('home.whyChooseUsSubtitle')}</p>
          </div>

          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 sm:gap-6 lg:grid-cols-3 lg:gap-8">
            {FEATURES.map((f) => (
              <div
                key={f.n}
                className="group relative aspect-[4/5] overflow-hidden rounded-3xl bg-[#3A2A20] shadow-md transition-shadow duration-300 hover:shadow-xl"
              >
                <img
                  src={f.img}
                  alt={t(f.altKey)}
                  loading="lazy"
                  className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                <div className="absolute inset-x-0 bottom-0 p-6">
                  <h3 className="font-['Fraunces'] text-2xl font-semibold text-white">
                    {t(`home.feature${f.n}Title`)}
                  </h3>
                  <p className="mt-2 text-sm leading-relaxed text-white/85">{t(`home.feature${f.n}Desc`)}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Services Banner */}
        <div className="bg-[#9A0002] py-12">

        </div>

        {/* Video Banner */}
        <VideoBanner />

        {/* Zipcode Modal */}
        <ZipcodeModal isOpen={modalOpen} onClose={() => setModalOpen(false)} />
      </div>
    </>
  );
};

export default HomePage;
